import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";
import { DismissibleBanner } from "@/components/shared/DismissibleBanner";

const BANNER_ID_COMPLETE_PROFILE = "complete-profile";
const BANNER_ID_PROFILE_PICTURE = "profile-picture";

function isOwnerOrAdmin(role: string): boolean {
  return role === "owner" || role === "admin";
}

function isInvitedUser(role: string): boolean {
  return role === "member" || role === "accountant";
}

function isProfileIncomplete(
  firstName: string | null,
  lastName: string | null,
  profileImage: string | null
): boolean {
  return (
    !firstName?.trim() ||
    !lastName?.trim() ||
    !profileImage
  );
}

/**
 * Renders dismissible profile-related banners: (1) for company owners/admins
 * who completed onboarding but have an incomplete profile; (2) for invited users
 * without a profile picture. At most one banner is shown.
 */
export function ProfileBanners(): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();

  if (!user?.tenantId || !user.id) return null;

  const { role, onboardingCompletedAt, firstName, lastName, profileImage } = user;

  if (
    isOwnerOrAdmin(role) &&
    onboardingCompletedAt != null &&
    isProfileIncomplete(firstName, lastName, profileImage)
  ) {
    return (
      <DismissibleBanner
        id={BANNER_ID_COMPLETE_PROFILE}
        userId={user.id}
        message={t("header.profileBanner.completeProfileMessage")}
        ctaLabel={t("header.profileBanner.completeProfileCta")}
        ctaTo="/profile/edit"
        variant="info"
      />
    );
  }

  if (isInvitedUser(role) && !profileImage) {
    return (
      <DismissibleBanner
        id={BANNER_ID_PROFILE_PICTURE}
        userId={user.id}
        message={t("header.profileBanner.profilePictureMessage")}
        ctaLabel={t("header.profileBanner.profilePictureCta")}
        ctaTo="/profile/edit"
        variant="info"
      />
    );
  }

  return null;
}
